import { Link } from 'react-router-dom'

export default function Terms() {
  return (
    <div className="py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-semibold text-text-primary mb-4">
          Terms of Service
        </h1>
        <p className="text-lg text-text-secondary mb-12">
          Last updated February 2026.
        </p>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            What Praxis is
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              Praxis CRM is a static web application that runs entirely in your browser. 
              It connects to your Google account to store data in your Google Drive.
            </p>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside">
              <li>We don't run servers that process your data</li>
              <li>We don't store your data anywhere</li>
              <li>We don't have user accounts or databases</li>
              <li>We provide the code; you run it locally</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Your responsibilities
          </h2>
          <div className="card">
            <ul className="space-y-3 text-sm text-text-secondary">
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  Google Account
                </span>
                <span>
                  You're responsible for the security of your Google account. 
                  We authenticate through Google, so your account security is your responsibility.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  Your data
                </span>
                <span>
                  Your CRM data lives in your Drive. You're responsible for backing it up 
                  if you want redundancy beyond what Google provides.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  Legal use
                </span>
                <span>
                  Use Praxis for lawful purposes. Don't use it to store or manage 
                  illegal content or facilitate illegal activities.
                </span>
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            What we provide
          </h2>
          <div className="card">
            <ul className="space-y-3 text-sm text-text-secondary">
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  Free forever
                </span>
                <span>
                  Praxis is free. No premium tiers. No "pro" features behind a paywall. 
                  The full application is available to everyone.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  As-is
                </span>
                <span>
                  We provide Praxis without warranties. It's open infrastructure, maintained 
                  in good faith, but we're not liable for business losses or data issues.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">
                  Updates
                </span>
                <span>
                  We may update the application. Since it's client-side, you'll get updates 
                  automatically when you load the page. Your Drive data remains yours.
                </span>
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Limitations of liability
          </h2>
          <div className="card bg-[#F5F5F3]">
            <p className="text-sm text-text-secondary mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside">
              <li>
                Praxis is provided "as is" without warranty of any kind, express or implied
              </li>
              <li>
                We're not liable for any indirect, incidental, special, consequential, 
                or punitive damages
              </li>
              <li>
                We're not responsible for any loss of data, profits, or business opportunities
              </li>
              <li>
                Our total liability is limited to the amount you paid us (which is zero)
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Third-party services
          </h2>
          <div className="card mb-4">
            <p className="text-sm text-text-secondary mb-4">
              Praxis integrates with external services. Your use of these services is governed 
              by their respective terms:
            </p>
            
            <h4 className="font-medium text-text-primary mb-2 mt-4">Google</h4>
            <ul className="space-y-2 text-sm text-text-secondary mb-4">
              <li>
                <a 
                  href="https://policies.google.com/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  Google Terms of Service
                </a>
              </li>
              <li>
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  Google Privacy Policy
                </a>
              </li>
              <li>
                <a 
                  href="https://www.google.com/drive/terms-of-service/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  Google Drive Terms of Service
                </a>
              </li>
            </ul>

            <h4 className="font-medium text-text-primary mb-2">AI Providers (optional)</h4>
            <p className="text-sm text-text-secondary mb-2">
              If you connect an AI provider, your use is subject to their terms:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary mb-4">
              <li>
                <a 
                  href="https://openai.com/policies/terms-of-use" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  OpenAI Terms of Use
                </a>
              </li>
              <li>
                <a 
                  href="https://www.anthropic.com/legal/consumer-terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  Anthropic Consumer Terms
                </a>
              </li>
              <li>
                <a 
                  href="https://ai.google.dev/gemini-api/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  Gemini API Terms
                </a>
              </li>
              <li>
                <a 
                  href="https://openrouter.ai/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-action-primary hover:underline"
                >
                  OpenRouter Terms
                </a>
              </li>
            </ul>

            <h4 className="font-medium text-text-primary mb-2">Companies House (UK)</h4>
            <p className="text-sm text-text-secondary">
              Prospect research may query the{' '}
              <a 
                href="https://developer-specs.company-information.service.gov.uk/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-action-primary hover:underline"
              >
                Companies House Public Data API
              </a>
              . This is publicly available UK government data.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            AI provider costs
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              If you use AI features, you are responsible for:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary list-disc list-inside">
              <li>
                <strong>Your API key:</strong> Obtaining, securing, and managing your own API key 
                from your chosen provider
              </li>
              <li>
                <strong>Usage costs:</strong> All AI API calls are billed directly to your account 
                by your provider. We have no visibility into your usage or costs.
              </li>
              <li>
                <strong>Rate limits:</strong> Managing your provider's rate limits and quotas
              </li>
              <li>
                <strong>Key security:</strong> If your API key is compromised, revoke it with your 
                provider immediately
              </li>
            </ul>
            <p className="text-sm text-text-secondary mt-4">
              Your API key is encrypted before storage using keys derived from your Google account. 
              We cannot access, recover, or reset your API key.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Changes to these terms
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary">
              We may update these terms occasionally. Since we don't have your email or any 
              way to contact you, changes will be posted here. Continued use of Praxis after 
              changes constitutes acceptance.
            </p>
          </div>
        </section>

        <section className="text-center pt-8 border-t border-border">
          <p className="text-text-secondary mb-4">
            Questions about privacy?{' '}
            <Link to="/privacy" className="text-action-primary hover:underline">
              Read how we structurally can't access your data
            </Link>
          </p>
          <Link to="/login" className="btn btn-primary">
            Get Started
          </Link>
        </section>
      </div>
    </div>
  )
}
